import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Badge, Nav } from "react-bootstrap";
import { getFamilyChangeRequestsSummary } from "../api/api";
import "./AppLayout.css";

const LOGO_SRC = "/logoamicale.png";

function MenuIcon({ name }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const icons = {
    profile: (
      <svg {...common}>
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    adherents: (
      <svg {...common}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    events: (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="18" rx="3" />
        <path d="M16 2v4" />
        <path d="M8 2v4" />
        <path d="M3 10h18" />
        <path d="M8 14h.01" />
        <path d="M12 14h.01" />
        <path d="M16 14h.01" />
      </svg>
    ),
    engagements: (
      <svg {...common}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M9 15l2 2 4-5" />
      </svg>
    ),
    forms: (
      <svg {...common}>
        <path d="M9 5h6" />
        <path d="M9 9h6" />
        <path d="M9 13h3" />
        <rect x="4" y="3" width="16" height="18" rx="2" />
      </svg>
    ),
    referentiels: (
      <svg {...common}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" />
        <path d="M8 7h8" />
        <path d="M8 11h8" />
      </svg>
    ),
    historique: (
      <svg {...common}>
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 3v6h6" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
    news: (
      <svg {...common}>
        <path d="M4 4h16v16H4z" />
        <path d="M8 8h8" />
        <path d="M8 12h8" />
        <path d="M8 16h5" />
      </svg>
    ),
    logs: (
      <svg {...common}>
        <path d="M4 5h16" />
        <path d="M4 12h16" />
        <path d="M4 19h10" />
        <path d="M8 3v4" />
        <path d="M8 10v4" />
        <path d="M8 17v4" />
      </svg>
    ),
    family: (
      <svg {...common}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 11h-6" />
        <path d="M19 8v6" />
      </svg>
    ),
  };

  return icons[name] || icons.profile;
}

export default function Sidebar() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.is_amical_admin || user.is_staff || user.is_superuser;
  const [familySummary, setFamilySummary] = useState({ pending: 0 });
  const [openGroups, setOpenGroups] = useState({
    general: true,
    gestion: true,
    activites: true,
    systeme: false,
  });

  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    getFamilyChangeRequestsSummary()
      .then((res) => {
        if (mounted) setFamilySummary(res.data || { pending: 0 });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [isAdmin]);

  const groups = useMemo(() => {
    if (!isAdmin) {
      return [
        {
          id: "general",
          label: "Accueil",
          items: [
            { to: "/dashboard/actualites", label: "Actualites", icon: "news" },
            { to: "/dashboard/historique", label: "Historique", icon: "historique" },
          ],
        },
        {
          id: "activites",
          label: "Services",
          items: [
            { to: "/dashboard/events-client", label: "Evenements", icon: "events" },
            { to: "/dashboard/engagements", label: "Engagements", icon: "engagements" },
          ],
        },
      ];
    }

    return [
      {
        id: "general",
        label: "Accueil",
        items: [
          { to: "/dashboard/actualites", label: "Actualites", icon: "news" },
          { to: "/dashboard/historique", label: "Historique", icon: "historique" },
        ],
      },
      {
        id: "gestion",
        label: "Adherents",
        items: [
          { to: "/dashboard/adherents", label: "Adherents", icon: "adherents" },
          {
            to: "/dashboard/family-requests",
            label: "Demandes famille",
            icon: "family",
            badge: familySummary.pending || 0,
          },
        ],
      },
      {
        id: "activites",
        label: "Activites",
        items: [
          { to: "/dashboard/events", label: "Evenements", icon: "events" },
          { to: "/dashboard/engagements", label: "Engagements", icon: "engagements" },
          { to: "/dashboard/form-templates", label: "Formulaires", icon: "forms" },
        ],
      },
      {
        id: "systeme",
        label: "Systeme",
        items: [
          { to: "/dashboard/referentiels", label: "Referentiels", icon: "referentiels" },
          { to: "/dashboard/audit-logs", label: "Logs admin", icon: "logs" },
        ],
      },
    ];
  }, [isAdmin, familySummary.pending]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <aside className="app-sidebar">
      <div className="app-sidebar-brand">
        <img src={LOGO_SRC} alt="Logo Amicale Autoroutes" />
        <div>
          <span>Menu</span>
          <strong>Amicale</strong>
          <Badge bg={isAdmin ? "warning" : "success"} text={isAdmin ? "dark" : undefined}>
            {isAdmin ? "Admin" : "Adherent"}
          </Badge>
        </div>
      </div>

      <div className="app-sidebar-menu">
        <Nav className="flex-column">
          {groups.map((group) => (
            <div className="app-sidebar-group" key={group.id}>
              <button
                className="app-sidebar-group__toggle"
                type="button"
                onClick={() => toggleGroup(group.id)}
              >
                <span>{group.label}</span>
                <strong>{openGroups[group.id] ? "-" : "+"}</strong>
              </button>

              {openGroups[group.id] && group.items.map((item) => (
                <Nav.Link key={item.to} as={NavLink} to={item.to} className="app-sidebar-link">
                  <span className="app-sidebar-link-icon">
                    <MenuIcon name={item.icon} />
                  </span>
                  {item.label}
                  {item.badge > 0 && (
                    <Badge bg="warning" text="dark" className="ms-auto">
                      {item.badge}
                    </Badge>
                  )}
                </Nav.Link>
              ))}
            </div>
          ))}
        </Nav>
      </div>
    </aside>
  );
}
